import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { FormFieldWrapper } from '../../shared/components';

/** Which of the job's own contacts is its client — `work_jobs.client_type` (V36). */
export type ClientType = 'BUILDER' | 'ARCHITECT' | 'OWNER';

/** In the order the screens offer them. A job's client defaults to its owner. */
export const CLIENT_TYPES: Array<{ value: ClientType; label: string }> = [
  { value: 'BUILDER', label: 'Builder' },
  { value: 'ARCHITECT', label: 'Architect' },
  { value: 'OWNER', label: 'Owner' },
];

export const DEFAULT_CLIENT_TYPE: ClientType = 'OWNER';

export const clientTypeLabel = (type: string | null | undefined) =>
  CLIENT_TYPES.find((t) => t.value === type)?.label ?? 'Owner';

interface ClientTypeFieldProps {
  value: ClientType;
  onChange: (type: ClientType) => void;
  /** The name picked for each contact on this job, or empty when that contact is not set. */
  names: Record<ClientType, string | null | undefined>;
  /** `above` puts the label over the box, as the Add Project fields read; default is MUI's in-box label. */
  labelPlacement?: 'floating' | 'above';
}

/**
 * The job's client: not a contact of its own any more but one of the three already on the job
 * (V36). Each option names the contact it would make the client; a contact the job does not
 * have yet cannot be chosen, so the client is never left pointing at nobody.
 *
 * Sits after the Builder, Architect and Owner pickers, since it chooses among them. The
 * quotation is addressed to whoever is picked here.
 */
export default function ClientTypeField({ value, onChange, names, labelPlacement = 'floating' }: ClientTypeFieldProps) {
  const above = labelPlacement === 'above';
  const chosenName = names[value];
  const field = (
    <TextField
      select
      size='small'
      fullWidth
      label={above ? undefined : 'Client'}
      value={value}
      onChange={(e) => onChange(e.target.value as ClientType)}
      helperText={chosenName ? `The quotation is addressed to ${chosenName}.` : `Pick the ${clientTypeLabel(value).toLowerCase()} above first.`}
      error={!chosenName}
    >
      {CLIENT_TYPES.map((t) => (
        <MenuItem key={t.value} value={t.value} disabled={!names[t.value] && t.value !== value}>
          {t.label}
          {names[t.value] ? ` — ${names[t.value]}` : ' (not set on this job)'}
        </MenuItem>
      ))}
    </TextField>
  );
  return above ? <FormFieldWrapper label='Client*'>{field}</FormFieldWrapper> : field;
}
